/**
 * API Configuration utility
 * Determines the correct API base URL based on the current domain
 */

const trimTrailingSlash = (value) => value.replace(/\/+$/, '');

const isTunnelHostname = (hostname) => (
  hostname.endsWith('.ngrok-free.dev') ||
  hostname.endsWith('.ngrok.app')
);

const isLocalApiUrl = (value) => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(value);

const getConfiguredApiUrl = () => {
  const configured = import.meta.env.VITE_API_URL;

  if (typeof configured !== 'string' || !configured.trim()) {
    return null;
  }

  const normalized = trimTrailingSlash(configured.trim());

  if (
    typeof window !== 'undefined' &&
    isTunnelHostname(window.location.hostname) &&
    isLocalApiUrl(normalized)
  ) {
    return `${window.location.origin}/api`;
  }

  return normalized;
};

const ensureApiSuffix = (baseUrl) => (
  baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`
);

const stripApiSuffix = (baseUrl) => (
  baseUrl.endsWith('/api') ? baseUrl.slice(0, -4) : baseUrl
);

/**
 * Get the base URL (without /api suffix) for OAuth redirects
 * Apple OAuth callbacks need the backend base URL without /api
 * @returns {string} Base URL without /api suffix
 */
export const getBackendBaseUrl = () => {
  const configuredApiUrl = getConfiguredApiUrl();

  if (configuredApiUrl) {
    if (configuredApiUrl.startsWith('/')) {
      return window.location.origin;
    }

    return stripApiSuffix(configuredApiUrl);
  }

  if (isTunnelHostname(window.location.hostname)) {
    return window.location.origin;
  }

  const currentDomain = window.location.hostname;

  // Local development - use local backend via nginx
  if (currentDomain === 'jetogo.local') {
    return 'https://jetogo.local';
  }

  // Localhost development
  if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
    return 'http://localhost:3002';
  }

  // Dev server
  if (currentDomain === 'dev.jetogo.com') {
    return 'https://api-dev.jetogo.com';
  }

  // Production
  if (currentDomain === 'jetogo.com' || currentDomain === 'www.jetogo.com') {
    return 'https://api.jetogo.com';
  }

  // Render.com frontend deployment
  if (currentDomain === 'jetogo-frontend.onrender.com') {
    return 'https://jetogo-backend.onrender.com';
  }

  // Default fallback
  return 'https://jetogo-backend.onrender.com';
};

/**
 * Get the base API URL based on current domain
 * @returns {string} Base API URL
 */
export const getBaseApiUrl = () => {
  const configuredApiUrl = getConfiguredApiUrl();

  if (configuredApiUrl) {
    return ensureApiSuffix(configuredApiUrl);
  }

  const currentDomain = window.location.hostname;

  if (isTunnelHostname(currentDomain)) {
    return `${window.location.origin}/api`;
  }

  // Check if we're on jetogo.local (development/local - use local backend via nginx)
  if (currentDomain === 'jetogo.local') {
    return 'https://jetogo.local/api';
  }
  
  // Check if we're on localhost (development)
  if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
    return 'http://localhost:3002/api';
  }

  // Check if we're on dev.jetogo.com (development server)
  if (currentDomain === 'dev.jetogo.com') {
    return 'https://api-dev.jetogo.com/api';
  }

  // Check if we're on admin.jetogo.com (admin portal)
  if (currentDomain === 'admin.jetogo.com') {
    return 'https://admin-api.jetogo.com/api';
  }

  // For production jetogo.com domains
  if (currentDomain === 'jetogo.com' || currentDomain === 'www.jetogo.com') {
    return 'https://api.jetogo.com/api';
  }

  // For Render.com frontend deployment
  if (currentDomain === 'jetogo-frontend.onrender.com') {
    return 'https://jetogo-backend.onrender.com/api';
  }

  // For any other domain, use the production backend URL
  return 'https://jetogo-backend.onrender.com/api';
};

/**
 * Get CORS settings based on current domain
 * @returns {object} CORS configuration
 */
export const getCorsSettings = () => {
  // Always use credentials for cross-origin requests
  // This is required for httpOnly refresh token cookies to work
  return {
    withCredentials: true
  };
};

/**
 * Get the full API URL for a specific endpoint
 * @param {string} path - API endpoint path (e.g., '/auth', '/users')
 * @returns {string} Full API URL
 */
export const getApiUrl = (path = '') => {
  const baseUrl = getBaseApiUrl();
  const fullUrl = `${baseUrl}${path}`;
  return fullUrl;
};

// Export the base URL for backward compatibility
export const BASE_API_URL = getBaseApiUrl();